import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import "./TopicDetailsPage.css";
import * as Layout from "common/layout";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroller";
import useInfiniteQuery from "hooks/useInfiniteQuery";

// graphql
import GET_TOPIC_DETAILS from "graphql/queries/GetTopicDetails";
import GET_BOOKABLE_CLASSES from "graphql/queries/GetBookableClasses";

import TopicDetails from "pages/class_details/TopicDetails";
import { Row, Alert } from "antd";
import ClassList from "common/class_card/ClassList";
import { CenteredLoading } from "common/loading";
import useBannerBackground from "hooks/useBannerBackground";
import { useParams } from "react-router-dom";

// custom types
import { CityState, City } from "types";

// style
import { SectionTitle } from "common/text";
import {
  FilterLabel,
  CityFilter,
  CityOption,
} from "pages/book_a_class/ClassFilterStyle";
import { CategoryLabel } from "./CategoryLabel";

const {
  PageContent,
  HeadingTitle,
  HeadingWrapper,
  SubHeading,
  HeadingGroup,
  HeaderCol,
  SectionHeader,
} = Layout;

const DEFAULT_PAGE_SIZE = 4;

function TopicDetailsPage() {
  const params = useParams();
  const [filter, setFilter] = useState({
    topic: {
      id: "0",
    },
    cities: [] as CityState[],
  });

  const searchParams = {
    topicId: filter.topic.id,
    cities: filter.cities
      .filter((city: CityState) => city.chosen)
      .map((city: CityState) => city.name),
    first: DEFAULT_PAGE_SIZE,
  };

  /** DATA MANAGEMENT FOR CLASS DETAILS */
  const {
    loading,
    data,
    error,
    refetch: refetchTopicDetails,
  } = useQuery(GET_TOPIC_DETAILS, {
    variables: {
      id: params.topicId,
    },
  });

  const {
    nodes: bookableClasses,
    error: bookableClassesError,
    pageInfo,
    loadMore,
  } = useInfiniteQuery("bookableClasses", GET_BOOKABLE_CLASSES, {
    variables: searchParams,
    skip: filter.topic.id === "0",
  });
  const { t } = useTranslation("topic_details");

  if (filter.topic.id === "0" && data) {
    const cities = data.topicDetails.topic.cities.map((city: City) => ({
      id: city.id,
      name: city.name,
      chosen: true,
    }));
    setFilter({
      ...filter,
      topic: {
        id: params.topicId || "",
      },
      cities,
    });
  }

  const { backgroundImage } = useBannerBackground();
  /** STATE MANAGEMENT FOR MODALS */
  // Add error screen if there's an error
  if (error) {
    console.log(error);
    return (
      <Alert
        type="error"
        style={{ marginTop: "64px" }}
        message="Error. Please check connection or log in again."
        description={error.graphQLErrors
          .map(({ message }) => message)
          .join(". ")}
      />
    );
  }

  if (bookableClassesError) {
    return (
      <Alert
        type="error"
        message="Error loading classes. Please check your internet or let us know if this keeps happening."
      />
    );
  }

  // Add loading skeleton when data is being fetched
  if (loading) return <div style={{ marginTop: "64px" }}>Loading data...</div>;

  const { topic, myTopic } = data.topicDetails;

  // HELPER FUNCTIONS
  const isOptionChosen = (name: string, arr: CityState[]) => {
    const findResult = arr.find((option: CityState) => {
      return option.name === name;
    });
    return findResult?.chosen as boolean;
  };

  const onChangeCities = (name: string) => {
    const newCities = [...filter.cities];
    const chosenIndex = newCities.findIndex((city: CityState) => {
      return city.name === name;
    });

    newCities[chosenIndex].chosen = !newCities[chosenIndex].chosen;
    setFilter({
      ...filter,
      cities: newCities,
    });
  };

  return (
    <PageContent>
      <HeadingWrapper
        style={{ justifyContent: "space-between", ...backgroundImage }}
      >
        <HeadingGroup>
          <SubHeading>{topic.chapter.name}</SubHeading>
          <HeadingTitle className="topic-heading">{topic.title}</HeadingTitle>
        </HeadingGroup>
        <CategoryLabel>{topic.category.name}</CategoryLabel>
      </HeadingWrapper>
      <TopicDetails
        topic={topic}
        myTopic={myTopic}
        refresh={refetchTopicDetails}
      />
      <div className="past-classes section">
        <SectionHeader>
          <HeaderCol xs={24} sm={24} md={17}>
            <SectionTitle>
              {t("Book a class with this topic").toUpperCase()}
            </SectionTitle>
          </HeaderCol>
          <HeaderCol xs={24} sm={24} md={7}>
            <CityFilter>
              <FilterLabel>{t("CITY VERSIONS")}:</FilterLabel>
              {filter.cities.map((city: City) => (
                <CityOption
                  key={city.id}
                  data-chosen={isOptionChosen(city.name, filter.cities)}
                  onClick={() => {
                    onChangeCities(city.name);
                  }}
                >
                  {city.name}
                </CityOption>
              ))}
            </CityFilter>
          </HeaderCol>
        </SectionHeader>
        <InfiniteScroll
          pageStart={0}
          loadMore={loadMore}
          hasMore={pageInfo?.hasNextPage}
          loader={<CenteredLoading key={pageInfo?.startCursor} />}
        >
          <Row className="section-cards" gutter={24}>
            <ClassList lessons={bookableClasses || []} allowsBooking />
          </Row>
        </InfiniteScroll>
      </div>
    </PageContent>
  );
}

export default TopicDetailsPage;
